/**
 * Custom Inventory - Local Data Version
 *
 * Version: 5.0
 *
 * This version reads inventory data from Liquid-rendered DOM data.
 * NO API CALLS to Worker or Shopify.
 *
 * The data comes from:
 * - ownStock: variant.inventory_quantity (Shopify native)
 * - externalStock: variant.metafields.custom.external_stock (synced via script)
 *
 * This is rendered in product-card.liquid in the .product-card-variants-data JSON.
 */

(function() {
  'use strict';

  // ==========================================================================
  // TRANSLATIONS HELPER
  // ==========================================================================

  function t(key) {
    return window.InventoryTranslations?.[key] || key;
  }

  // ==========================================================================
  // INVENTORY LEVELS
  // ==========================================================================

  const INVENTORY_LEVELS = {
    IN_STOCK: 'in_stock',
    EXTENDED_IN_STOCK: 'extended_in_stock',
    BACKORDERED: 'backordered',
    NONE: 'none'
  };

  // ==========================================================================
  // DELIVERY TIME TRANSLATION
  // ==========================================================================

  const DELIVERY_TIME_UNITS = {
    arkipäivää: { fi: 'arkipäivää', sv: 'arbetsdagar', en: 'weekdays', addDays: 2 },
    viikkoa: { fi: 'viikkoa', sv: 'veckor', en: 'weeks', addDays: 0 },
    kuukautta: { fi: 'kuukautta', sv: 'månader', en: 'months', addDays: 0 }
  };

  function getLocale() {
    // Try Shopify.locale first
    if (typeof Shopify !== 'undefined' && Shopify.locale) {
      const locale = Shopify.locale.toLowerCase();
      if (locale.startsWith('sv')) return 'sv';
      if (locale.startsWith('en')) return 'en';
      if (locale.startsWith('fi')) return 'fi';
    }

    // Try document.documentElement.lang
    const htmlLang = document.documentElement.lang?.toLowerCase() || '';
    if (htmlLang.startsWith('sv')) return 'sv';
    if (htmlLang.startsWith('en')) return 'en';
    if (htmlLang.startsWith('fi')) return 'fi';

    // Try to detect from URL path (e.g., /sv/, /en-int/)
    const pathMatch = window.location.pathname.match(/^\/(sv|en|fi)(?:[-_]|\/|$)/i);
    if (pathMatch) {
      const pathLocale = pathMatch[1].toLowerCase();
      if (pathLocale === 'sv') return 'sv';
      if (pathLocale === 'en') return 'en';
      if (pathLocale === 'fi') return 'fi';
    }

    return 'fi';
  }

  function translateDeliveryTime(deliveryTime) {
    if (!deliveryTime) return '';

    const locale = getLocale();

    // Debug logging (remove after testing)
    if (window.location.search.includes('debug=inventory')) {
      console.log('[CustomInventory] translateDeliveryTime:', {
        input: deliveryTime,
        locale: locale,
        shopifyLocale: typeof Shopify !== 'undefined' ? Shopify.locale : 'undefined',
        htmlLang: document.documentElement.lang
      });
    }

    if (locale === 'fi') return deliveryTime;

    const trimmed = deliveryTime.trim().toLowerCase();
    let matchedUnit = null;

    for (const unit of Object.keys(DELIVERY_TIME_UNITS)) {
      if (trimmed.includes(unit)) {
        matchedUnit = unit;
        break;
      }
    }

    if (!matchedUnit) {
      if (window.location.search.includes('debug=inventory')) {
        console.log('[CustomInventory] No matching unit found for:', deliveryTime);
      }
      return deliveryTime;
    }

    const unitConfig = DELIVERY_TIME_UNITS[matchedUnit];
    const numberPart = trimmed.replace(matchedUnit, '').trim();
    const rangeMatch = numberPart.match(/(\d+)\s*[-–—]\s*(\d+)/);
    const singleMatch = numberPart.match(/^(\d+)$/);

    let min, max;
    if (rangeMatch) {
      min = parseInt(rangeMatch[1], 10);
      max = parseInt(rangeMatch[2], 10);
    } else if (singleMatch) {
      min = max = parseInt(singleMatch[1], 10);
    } else {
      return deliveryTime;
    }

    const translatedUnit = unitConfig[locale] || unitConfig.en;
    if (unitConfig.addDays > 0) {
      min += unitConfig.addDays;
      max += unitConfig.addDays;
    }

    const result = min === max ? `${min} ${translatedUnit}` : `${min}-${max} ${translatedUnit}`;

    if (window.location.search.includes('debug=inventory')) {
      console.log('[CustomInventory] Translation result:', result);
    }

    return result;
  }

  // ==========================================================================
  // GET VARIANTS DATA FROM DOM
  // ==========================================================================

  const variantsDataCache = new WeakMap();

  function getVariantsData(productCard) {
    if (!productCard) return {};

    if (variantsDataCache.has(productCard)) {
      return variantsDataCache.get(productCard);
    }

    let data = {};
    const el = productCard.querySelector('.product-card-variants-data');
    if (el) {
      try {
        data = JSON.parse(el.textContent);
      } catch (e) {
        // Silent fail
      }
    }

    variantsDataCache.set(productCard, data);
    return data;
  }

  // ==========================================================================
  // STOCK STATUS LOGIC
  // ==========================================================================

  function getStockStatus({ ownStock, externalStock, inventoryPolicy, deliveryTime, isExtendedAssortment, isBundle, variantAvailable }) {
    let level, statusText, urgencyText = '', isAvailable = true;

    const translatedDeliveryTime = translateDeliveryTime(deliveryTime);

    if (isBundle) {
      if (variantAvailable) {
        return { level: INVENTORY_LEVELS.IN_STOCK, statusText: t('inStock'), urgencyText: '', isAvailable: true };
      } else {
        return { level: INVENTORY_LEVELS.NONE, statusText: t('noStock'), urgencyText: '', isAvailable: false };
      }
    }

    if (isExtendedAssortment) {
      if (ownStock > 0) {
        level = INVENTORY_LEVELS.IN_STOCK;
        statusText = t('inStock');
      } else if (externalStock > 0) {
        level = INVENTORY_LEVELS.EXTENDED_IN_STOCK;
        if (translatedDeliveryTime) {
          statusText = t('orderProductDelivery');
          urgencyText = translatedDeliveryTime;
        } else {
          statusText = t('orderProduct');
        }
      } else {
        level = INVENTORY_LEVELS.NONE;
        statusText = t('temporarilyOut');
        isAvailable = false;
      }
    } else {
      if (ownStock > 0) {
        level = INVENTORY_LEVELS.IN_STOCK;
        statusText = t('inStock');
      } else if (inventoryPolicy === 'continue') {
        level = INVENTORY_LEVELS.BACKORDERED;
        statusText = translatedDeliveryTime || t('deliveryConfirmed');
      } else {
        level = INVENTORY_LEVELS.NONE;
        statusText = t('temporarilyOut');
        isAvailable = false;
      }
    }

    return { level, statusText, urgencyText, isAvailable };
  }

  // ==========================================================================
  // UPDATE CARD UI
  // ==========================================================================

  function updateCardUI(card, status) {
    const textEl = card.querySelector('.js-inventory-notice');
    const urgencyEl = card.querySelector('.js-inventory-urgency');

    if (textEl) {
      textEl.textContent = status.statusText;
    }

    if (urgencyEl) {
      if (status.urgencyText) {
        // No leading space needed - locale text already has trailing space
        urgencyEl.textContent = status.urgencyText;
        urgencyEl.hidden = false;
      } else {
        urgencyEl.textContent = '';
        urgencyEl.hidden = true;
      }
    }

    card.dataset.inventoryLevel = status.level;
  }

  // ==========================================================================
  // SWATCH CHANGE LISTENER
  // ==========================================================================

  function initCardSwatchListener() {
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (!input.matches('product-card .js-option, .findify-product-card .js-option')) return;

      const productCard = input.closest('product-card') || input.closest('.findify-product-card');
      if (!productCard) return;

      const inventoryEl = productCard.querySelector('.product-inventory--card');
      if (!inventoryEl) return;

      const variantId = input.dataset.variantId;
      if (!variantId) return;

      const variantsData = getVariantsData(productCard);
      const variantData = variantsData[variantId];

      if (!variantData) return;

      const isExtendedAssortment = inventoryEl.dataset.isExtendedAssortment === 'true';
      const isBundle = inventoryEl.dataset.isBundle === 'true';

      // Update variant ID on the card
      inventoryEl.dataset.variantId = variantId;

      // Get stock from DOM data
      const ownStock = variantData.ownStock || 0;
      const externalStock = variantData.externalStock || 0;
      const inventoryPolicy = variantData.inventoryPolicy || 'deny';
      const deliveryTime = variantData.deliveryTime || '';
      const variantAvailable = variantData.available;

      const status = getStockStatus({
        ownStock,
        externalStock,
        inventoryPolicy,
        deliveryTime,
        isExtendedAssortment,
        isBundle,
        variantAvailable
      });

      updateCardUI(inventoryEl, status);
    });
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  function init() {
    initCardSwatchListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ==========================================================================
  // GLOBAL API (simplified - for compatibility)
  // ==========================================================================

  window.CustomInventoryAPI = {
    INVENTORY_LEVELS,
    getStockStatus,
    translateDeliveryTime,
    getLocale,

    // No-op functions for compatibility
    fetchVariantInventory: () => Promise.resolve({ ownStock: 0, externalStock: 0 }),
    fetchProductInventory: () => Promise.resolve({}),
    fetchBatchInventory: () => Promise.resolve({}),
    clearCache: () => {},
    refreshCardInventory: () => {},
    rescanCards: () => {}
  };

})();
